import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import Nav from "../../hooks/Nav";

import Table from "../../tailwindUI/Table";
import Pagination from "../../tailwindUI/Pagination";
import PrimaryButton from "../../tailwindUI/PrimaryButton";
import SearchBar from "../../tailwindUI/SearchBar";

import { FixatServiceSlideForm } from "./FixatServiceSlideForm";
import { ModalNotification } from "../modals/ModalNotification";
import SecondaryLoader from "../../hooks/SecondaryLoader";
import { useFixatServices } from "../../hooks/catalogs/useFixatServices";
import {
  AdjustmentsHorizontalIcon,
  EyeIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import Filters from "../../tailwindUI/Filters";
import { useFilters } from "../../hooks/useFilters";
import { filterValues } from "./fixatServicesData";
import SlideOver from "../../tailwindUI/SlideOver";
import PermissionsGate from "../../cognito/permissions/PermissionsGate";
import { allowedRolesForRoutes } from "../../routes/SideBarRoutes";
import { AuthContext } from "../../cognito/AuthProvider";

const tableColumns = [
  { heading: "Sku", value: "sku" },
  { heading: "Nombre", value: "name", main: true },
  { heading: "Precio", value: "price_formatted" },
  { heading: "Tipo persona", value: "legal_type_formatted" },
  { heading: "Periodicidad", value: "periodicity_formatted" },
  { heading: "Estatus", value: "status_formatted" },
];

export function FixatServices() {
  const { user } = useContext(AuthContext);
  const canEditServices = user.roles.some((role) =>
    allowedRolesForRoutes.editServiceRoles.includes(role)
  );

  const history = useHistory();

  const [openSlideForm, setOpenSlideForm] = useState(false);
  const [openSlideFilters, setOpenSlideFilters] = useState(false);

  const [searchInput, setSearchInput] = useState(null);
  const {
    filters,
    setFilters,
    setFiltersApplied,
    filterString,
    handleCleanFilters,
  } = useFilters(filterValues);

  const {
    services,
    isLoadingData,
    isUpdatingData,
    updateServiceStatus,
    addService,
    editService,
    serviceToEdit,
    setServiceToEdit,
    showModal,
    setShowModal,
    pagination,
    currentPage,
    setCurrentPage,
  } = useFixatServices({ searchInput, filterString });

  const handleViewService = (serviceToView) => {
    const service = Object.fromEntries(
      Object.entries(serviceToView).filter(
        ([key]) => key !== "status_formatted"
      )
    );
    history.push(`/services/view/${service.id}`, {
      service,
    });
  };

  const handleEditService = (service) => {
    setServiceToEdit(service);
    setOpenSlideForm(true);
  };

  const columnActions = [
    {
      id: 1,
      name: "Visualizar",
      type: "primary",
      icon: <EyeIcon className="w-5 h-5 text-gray-600 lg:text-white" />,
      action: handleViewService,
    },
    ...(canEditServices
      ? [
          {
            id: 2,
            name: "Editar servicio",
            type: "secondary",
            icon: (
              <PencilSquareIcon className="w-5 h-5 text-gray-600 lg:text-gray-600" />
            ),
            action: handleEditService,
            disabled: true,
          },
        ]
      : []),
  ];
  return (
    <>
      {isUpdatingData && <SecondaryLoader />}
      <div className="w-full h-full pb-8">
        <div className="w-full xs:h-full md:h-full space-y-4">
          <div className="w-full bg-white xs:h-auto md:h-full">
            <Nav>
              <div className="w-full">
                <span className="text-3xl text-white font-bold">
                  Servicios FIXAT
                </span>
              </div>
            </Nav>
          </div>

          <div className="w-full px-4 pt-20 lg:pt-3">
            <div className="flex justify-end lg:justify-between items-center w-full">
              <h1 className="hidden lg:block text-4xl font-bold xs:text-center md:text-left">
                Servicios FIXAT
              </h1>

              <div className="w-full lg:w-[15rem]">
                <PermissionsGate
                  allowedRoles={allowedRolesForRoutes.createServiceRoles}
                >
                  <PrimaryButton
                    isFullWidth={true}
                    onClick={() => {
                      setServiceToEdit(null);
                      setOpenSlideForm(true);
                    }}
                  >
                    Crear servicio
                    <PlusIcon className="w-5 h-5 ml-2 inline-block" />
                  </PrimaryButton>
                </PermissionsGate>
              </div>
            </div>
          </div>

          <div className="w-full px-4 pt-4 pb-2">
            <SearchBar
              value={searchInput}
              setValue={setSearchInput}
              placeholder_desktop="Nombre, SKU"
            />
            <div className="w-full flex justify-end gap-4 pt-6">
              <div className="flex items-center gap-6">
                {filterString !== "" && (
                  <span
                    className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 flex gap-1 pt-0 md:pt-[1px]"
                    onClick={handleCleanFilters}
                  >
                    Limpiar filtros <TrashIcon className="h-5 w-5" />
                  </span>
                )}
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm font-medium text-gray-700 md:hidden"
                  onClick={() => setOpenSlideFilters(true)}
                >
                  Filtros{" "}
                  <span>
                    <AdjustmentsHorizontalIcon className="w-[18px] h-[18px]" />
                  </span>
                </button>
                <Filters
                  filters={filters}
                  setFilters={setFilters}
                  setFiltersApplied={setFiltersApplied}
                />
              </div>
            </div>
          </div>

          <div className="w-full px-4 pt-4 lg:pt-0 pb-4">
            <Table
              title="Servicios FIXAT"
              columns={tableColumns}
              actions={columnActions}
              data={services}
              isLoadingData={isLoadingData}
            />
          </div>

          {services && services.length > 0 && (
            <div className="lg:flex-1 lg:flex items-center justify-between px-4">
              {pagination && pagination.totalItems > 0 && (
                <div className="flex items-center gap-4">
                  <p className="select-none text-gray-700 hidden lg:block lg:text-nowrap">
                    Mostrando{" "}
                    {pagination.pages
                      .find((element) => element.page === currentPage - 1)
                      ?.range.join(" a ")}{" "}
                    de {pagination.totalItems} resultados
                  </p>
                </div>
              )}
              <div>
                <Pagination
                  pages={pagination?.totalPages || pagination?.pages?.length}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <FixatServiceSlideForm
        openSlideForm={openSlideForm}
        setOpenSlideForm={setOpenSlideForm}
        serviceToEdit={serviceToEdit}
        addService={addService}
        editService={editService}
      />
      <SlideOver
        open={openSlideFilters}
        setOpen={setOpenSlideFilters}
        title="Filtros"
      >
        <Filters
          filters={filters}
          openSlide={openSlideFilters}
          setFilters={setFilters}
          setOpenSlide={setOpenSlideFilters}
          setFiltersApplied={setFiltersApplied}
        />
      </SlideOver>
      <ModalNotification
        type="confirm"
        showModal={showModal}
        setShowModal={setShowModal}
        title="¿Estás seguro de modificar el estatus?"
        description=""
        onConfirm={updateServiceStatus}
      />
    </>
  );
}
